/**
 * Due-date presets for a next move (#114): Today 5 pm, Next business day 9 am, +2 business days, A week, or a picked
 * date-time. Business days skip Saturday and Sunday (holidays are not modelled). Local time, as the person reads it.
 */
export type NextMovePreset = 'today' | 'next-business-day' | 'two-business-days' | 'week'

export const PRESET_LABEL: Record<NextMovePreset, string> = {
  today: 'Today 5 pm',
  'next-business-day': 'Next business day 9 am',
  'two-business-days': '+2 business days',
  week: 'A week',
}

const isWeekend = (d: Date) => d.getDay() === 0 || d.getDay() === 6

function at(date: Date, hour: number): Date {
  const out = new Date(date)
  out.setHours(hour, 0, 0, 0)
  return out
}

/** `n` business days after `from` (the day itself is never counted), at 9 am. */
export function addBusinessDays(from: Date, n: number, hour = 9): Date {
  const d = new Date(from)
  let left = n
  while (left > 0) {
    d.setDate(d.getDate() + 1)
    if (!isWeekend(d)) left -= 1
  }
  return at(d, hour)
}

export function dueForNextMovePreset(preset: NextMovePreset, now = new Date()): Date {
  if (preset === 'today') {
    const five = at(now, 17)
    // After 5 pm "today 5 pm" has passed: an hour from now keeps the move in the future.
    return five.getTime() > now.getTime() ? five : new Date(now.getTime() + 60 * 60 * 1000)
  }
  if (preset === 'next-business-day') return addBusinessDays(now, 1)
  if (preset === 'two-business-days') return addBusinessDays(now, 2)
  const week = new Date(now)
  week.setDate(week.getDate() + 7)
  return at(week, 9)
}

export function toLocalInput(date: Date): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 16)
}

export function fromLocalInput(local: string): string | null {
  if (!local) return null
  const d = new Date(local)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}
